


















import { Message } from 'base/store/state.interface'
import Vue, { PropType } from 'vue'
import BaseIcon from '../../../base/components/BaseIcon.vue'
import { EventService } from '../../../base/services/EventService'
import { store } from '../../../base/store'
import rafpoly from '../../../vendor/polyfills/requestanimationframe'
import { ChatService } from '../ChatService'
import MessageList from './MessageList.vue'

export default Vue.extend({
  components: {
    MessageList,
    BaseIcon
  },
  props: {
    channel: {
      type: Object as PropType<MessageChannel | undefined>,
      default: {}
    },
    messages: {
      type: Array as PropType<Message[]>,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      canFetchMessages: true,
      loadingMessages: false,
      threadParent: null,
      threadMessages: [] as Message[],
      showScrollToBottomFAB: false,
      numNewMessages: 0,
      isScrollAnimationActive: false
    }
  },
  created() {
    EventService.$on('socketservice--message-received', this.onReceiveMessage)
  },
  destroyed() {
    EventService.$off('socketservice--message-received', this.onReceiveMessage)
  },
  async mounted() {
    const container = this.$refs.container as HTMLElement
    container.addEventListener('scroll', this.onScroll)

    // when there are messages in the localstorage these are already displayed wile fetching new messages
    // so we scroll down before calling the api to leverage the user experience
    this.scrollToBottom()
    await this.fetchMessages()
    // scroll down in case new messages were added
    this.scrollToBottom()

    if (container.scrollHeight <= container.offsetHeight) {
      this.onScroll()
    }

    rafpoly()
  },
  watch: {
    messages(newValue: Message[], oldValue: Message[]) {
      if (this.loadingMessages) {
        return
      }

      if (this.showScrollToBottomFAB) {
        const numNewMessages = newValue.length - oldValue.length

        if (numNewMessages > 0) {
          this.numNewMessages += numNewMessages
        }

        return
      }

      // Scroll to bottom if messages have been added
      if (newValue.length > oldValue.length) {
        setImmediate(() => this.scrollToBottom(true))
      }
    }
  },
  computed: {
    numNewMessagesRendered(): string | number {
      const numNewMessages = this.numNewMessages

      if (numNewMessages > 99) {
        return '99+'
      }

      return numNewMessages
    },
    getMessagesForMessageList(): any {
      return this.threadMessages.length ? this.threadMessages : this.messages
    }
  },
  methods: {
    onShowScrollToBottomFabClick(): void {
      this.scrollToBottom(true)
    },
    /**
     * Scroll to bottom of chat to see latest messages
     */
    scrollToBottom(smoothScroll = false) {
      const container = this.$refs.container as HTMLElement
      const scrollHeight = container.scrollHeight

      if (!smoothScroll) {
        container.scrollTo(0, scrollHeight)
        return
      }

      const scrollDuration = 300

      const containerHeight = container.offsetHeight
      const scrollTop = container.scrollTop
      const scrollDistance = scrollTop + container.offsetHeight
      const remainingScrollDistance = scrollHeight - scrollDistance

      const cosParameter = remainingScrollDistance / 2
      let oldTimestamp = performance.now()
      let scrollCount = 0

      this.isScrollAnimationActive = true

      const step = (newTimestamp: number) => {
        scrollCount += Math.PI / (scrollDuration / (newTimestamp - oldTimestamp))

        if (scrollCount >= Math.PI) {
          container.scrollTo(0, scrollHeight)
        }

        if (container.scrollTop + containerHeight >= scrollHeight) {
          this.isScrollAnimationActive = false
          // the onscroll event was surpressed during the scroll animation, so we trigger it manually now
          this.onScroll()
          return
        }

        container.scrollTo(0, scrollTop + Math.round(cosParameter - cosParameter * Math.cos(scrollCount)))
        oldTimestamp = newTimestamp
        window.requestAnimationFrame(step)
      }

      window.requestAnimationFrame(step)
    },

    async onScroll() {
      if (this.isScrollAnimationActive) {
        return
      }

      const container = this.$refs.container as HTMLElement

      const scrollTop = container.scrollTop
      const scrollDistance = scrollTop + container.offsetHeight
      const scrollHeight = container.scrollHeight
      const containerIsScrolled = (scrollHeight > 0) && (scrollDistance < scrollHeight)

      // since the scroll event is triggered heavily when scrolling, make sure to trigger the setters only when necessary
      if (this.showScrollToBottomFAB !== containerIsScrolled) {
        this.showScrollToBottomFAB = containerIsScrolled
        if (!containerIsScrolled) {
          this.numNewMessages = 0
        }
      }

      if (!this.canFetchMessages || (scrollTop > 0)) {
        return
      }

      let firstMessageId

      if (this.messages && this.messages.length) {
        firstMessageId = this.messages[0].id
      }

      await this.fetchMessages(firstMessageId)
      container.scrollTo(0, container.scrollHeight - scrollHeight)
    },

    leaveThread() {
      this.showMessageThread(0)
      this.$emit('showMessageThread', 0)
    },

    countMessages(messages) {
      let count = 0
      for (let prop in messages) {
        if (!messages.hasOwnProperty(prop)) {
          continue
        }
        count++
      }
      return count
    },

    async onReceiveMessage(message: Message) {
      // check for thread handling
      const parent = this.threadParent

      if (
        !parent
        || (parent.message_channel_id !== message.message_channel_id)
        || (parent.id !== message.parent_id)
      ) {
        return
      }

      this.threadMessages.push(message)
    },

    async fetchMessages(firstMessageId = undefined) {
      if (!this.channel || !this.channel.id) {
        return
      }

      this.loadingMessages = true
      const reply = await ChatService.fetchMessages(this.channel.id, firstMessageId)

      for (let id in reply.parents) {
        if (!reply.parents.hasOwnProperty(id)) {
          continue
        }
        // messages that aren't part of `sorting` are only included in the reply since they are referenced
        // by another message. we add them to the cache and don't process them any further.
        void store.dispatch('setStoreMessage', reply.parents[id])
      }

      const numMessages = this.countMessages(reply.items)

      if (numMessages) {
        await store.dispatch('processMessages', reply.items)
      }

      this.canFetchMessages = numMessages >= ChatService.messageLimit
      this.loadingMessages = false
    },
    async showMessageThread(threadParentId) {
      this.threadParent = this.$store.state.message.items[threadParentId]

      if (!this.threadParent || !this.channel || !this.channel.id || !threadParentId) {
        this.threadMessages = []
        return
      }

      const reply = await ChatService.fetchThread(this.channel.id, threadParentId)
      const threadMessages: Message[] = []

      for (const prop in reply) {
        if (!reply.hasOwnProperty(prop)) {
          continue
        }
        threadMessages.push(reply[prop])
      }

      threadMessages.sort((a, b) => a.id - b.id)

      this.threadMessages = threadMessages
    }
  }
})
